import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as React from 'react';

import { Icons } from '@/components/icons';
import { cn } from '@/utils/cn';

const AccordionPrimitiveRoot = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('border-b', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    hideChevron?: boolean;
    justifyBetween?: boolean;
  }
>(
  (
    {
      className,
      children,
      hideChevron = false,
      justifyBetween = true,
      ...props
    },
    ref
  ) => (
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'group flex flex-1 items-center py-2 font-medium transition-all hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-tint focus-visible:ring-offset-2',
        justifyBetween && 'justify-between',
        className
      )}
      {...props}
    >
      {justifyBetween ? (
        <>
          {children}
          {!hideChevron && (
            <Icons.chevronDown
              className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
              aria-hidden
            />
          )}
        </>
      ) : (
        <div className="flex items-center gap-2">
          {children}
          {!hideChevron && (
            <Icons.chevronDown
              className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
              aria-hidden
            />
          )}
        </div>
      )}
    </AccordionPrimitive.Trigger>
  )
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content ref={ref} {...props}>
    <div className={cn('pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export {
  AccordionContent,
  AccordionItem,
  AccordionPrimitiveRoot,
  AccordionTrigger,
};
